/**
 * GA4 Conversion Tracking + Google Consent Mode v2 — TopRankPulse
 *
 * GDPR-first: gtag.js is loaded immediately so Google can use Consent
 * Mode v2 signals, but `ad_storage` and `analytics_storage` start as
 * "denied". No personal data is sent until the user accepts via the
 * cookie consent banner.
 *
 * Three launch-critical events:
 *   - view_snapshot_offer  → fired when /snapshot, /snapshot-9 or PricingPage loads
 *   - start_checkout       → fired right before redirect to Stripe
 *   - purchase             → fired on success-redirect after Stripe (uses real
 *                            amount + currency fetched from /api/payments/status)
 */
import axios from "axios";
import { API } from "../config";

const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;
const LS_PURCHASE_KEY = "trp_ga4_purchase_fired"; // session_id de-dup
const LS_CONSENT_KEY = "trp_cookie_consent_v1"; // 'granted' | 'denied'

function ensureDataLayer() {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
}

/**
 * Read the user's current consent decision, if any.
 * Returns `null` if no decision yet (banner should be shown).
 */
export function getStoredConsent() {
  try {
    return localStorage.getItem(LS_CONSENT_KEY); // 'granted' | 'denied' | null
  } catch {
    return null;
  }
}

/**
 * Persist consent choice and propagate it to gtag (Consent Mode v2).
 * Call this from the cookie banner buttons.
 */
export function setConsent(state /* 'granted' | 'denied' */) {
  try {
    localStorage.setItem(LS_CONSENT_KEY, state);
  } catch {
    /* ignore quota errors */
  }
  if (!GA4_ID || !window.gtag) return;
  window.gtag("consent", "update", {
    ad_storage: state,
    analytics_storage: state,
    ad_user_data: state,
    ad_personalization: state,
  });
  // Only fire the deferred purchase event after consent is granted so we
  // never beacon a return-from-Stripe event without permission.
  if (state === "granted") maybeFirePurchase();
}

let _initialized = false;

export function initAnalytics() {
  if (_initialized) return;
  if (!GA4_ID) return; // graceful no-op
  _initialized = true;

  ensureDataLayer();

  // Consent Mode v2 defaults — MUST be set BEFORE gtag('config').
  // Returning visitors who previously accepted bypass the denied default.
  const stored = getStoredConsent();
  const initialState = stored === "granted" ? "granted" : "denied";
  window.gtag("consent", "default", {
    ad_storage: initialState,
    analytics_storage: initialState,
    ad_user_data: initialState,
    ad_personalization: initialState,
    wait_for_update: 500, // give the banner ~500 ms to apply user choice
  });

  // Inject gtag.js once.
  if (!document.getElementById("ga4-gtag")) {
    const s = document.createElement("script");
    s.async = true;
    s.id = "ga4-gtag";
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(s);
  }

  window.gtag("js", new Date());
  // `send_page_view` left as default true → page_view auto-fires on init.
  window.gtag("config", GA4_ID, { transport_type: "beacon" });

  // If consent was already granted in a previous session, fire the
  // deferred purchase check now. Otherwise wait for setConsent('granted').
  if (initialState === "granted") maybeFirePurchase();
}

/**
 * Fire a GA4 event. Safe no-op if GA4 is disabled.
 */
export function trackEvent(eventName, params = {}) {
  if (!GA4_ID) return;
  ensureDataLayer();
  try {
    window.gtag("event", eventName, params);
  } catch (_) {
    /* never let analytics crash the app */
  }
}

/**
 * Detect ?payment=success&session_id=... on any landing route after
 * Stripe redirects back, fetch real amount+currency from backend,
 * fire `purchase` event, then de-dup by session_id in localStorage so
 * a hard refresh doesn't double-count.
 */
async function maybeFirePurchase() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("payment") !== "success") return;
    const sessionId = params.get("session_id");
    if (!sessionId) return;

    const fired = JSON.parse(localStorage.getItem(LS_PURCHASE_KEY) || "[]");
    if (fired.includes(sessionId)) return;

    const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
    // amount_total is in cents for real Stripe sessions, 0 for admin bypass.
    const value =
      typeof data?.amount_total === "number"
        ? data.amount_total / 100
        : undefined;
    const currency = (data?.currency || "eur").toUpperCase();

    if (data?.payment_status === "paid") {
      trackEvent("purchase", {
        transaction_id: sessionId,
        value,
        currency,
      });
      fired.push(sessionId);
      localStorage.setItem(LS_PURCHASE_KEY, JSON.stringify(fired.slice(-50)));
    }
  } catch (_) {
    /* swallow — analytics must never break the app */
  }
}
